// Motion that answers a change (docs/plans/app-motion.md): a number that rolls to its new
// value, a state that fades in over the one before, a row that arrives. Nothing here moves
// on the first render or on its own; the new value is in the page at once and motion only
// shows the way there. All of it is off when the device asks for reduced motion or the
// person chose "Reduced" (`data-motion="reduce"` on <html>).
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cx } from './cx.ts'

/** True when nothing should move: the device's setting, or the person's own choice. */
export function motionReduced(): boolean {
  if (typeof document === 'undefined') return true
  if (document.documentElement.dataset.motion === 'reduce') return true
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * A number that grows by one each time `value` changes after the first render, and stays 0
 * until then. Give it as a `key` to replay a CSS entrance on change only.
 */
export function useChangeCount(value: unknown): number {
  const last = useRef(value)
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (Object.is(last.current, value)) return
    last.current = value
    setCount((c) => c + 1)
  }, [value])
  return count
}

const ROLL_MS = 500

/**
 * A number shown through `format`. When it changes it rolls from the old value to the new
 * one in half a second. The page holds the final text as soon as React renders it; the roll
 * only rewrites the same text node on the way, so tests and copy-paste see the real value
 * once it settles, and there is never a second copy of the number in the page.
 */
export function RollingNumber({
  value,
  format,
  className,
}: {
  value: number
  format: (value: number) => string
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const shown = useRef(value)
  const formatRef = useRef(format)
  // The latest formatter, for the frames of a roll already running.
  useLayoutEffect(() => {
    formatRef.current = format
  })
  useLayoutEffect(() => {
    const node = ref.current?.firstChild
    const from = shown.current
    const to = value
    if (!node || from === to || motionReduced()) {
      shown.current = to
      return
    }
    let frame = 0
    const start = performance.now()
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ROLL_MS)
      // Ease out: quick at first, settling on the value.
      const eased = 1 - (1 - t) ** 3
      const current = Math.round(from + (to - from) * eased)
      shown.current = current
      node.nodeValue = formatRef.current(current)
      if (t < 1) frame = requestAnimationFrame(step)
    }
    node.nodeValue = formatRef.current(from)
    frame = requestAnimationFrame(step)
    return () => {
      cancelAnimationFrame(frame)
      node.nodeValue = formatRef.current(to)
      shown.current = to
    }
  }, [value])
  return (
    <span ref={ref} className={cx('tabular', className)}>
      {format(value)}
    </span>
  )
}

/**
 * Something that changes state in place (a badge, the sync sign): when `state` changes the
 * new one fades and settles in. On the first render it just appears.
 */
export function StateSwap({
  state,
  children,
  className,
}: {
  state: string
  children: ReactNode
  className?: string
}) {
  const count = useChangeCount(state)
  return (
    <span key={count} className={cx('inline-flex', count > 0 && 'animate-swap-in', className)}>
      {children}
    </span>
  )
}

const ARRIVAL_MS = 1400

/**
 * The ids of rows that appeared after the list was first shown (from a sync, or another
 * device), for a moment. Rows there get `arrivalClass` so a new bill or ticket is noticed.
 * Pass `ready` false while the list is still loading, so the rows it loads with do not
 * count as arrivals, and `scope` (the search, filter and page) so that a list the person
 * asked for does not count as rows arriving: a new scope starts a new baseline.
 */
export function useArrivals(ids: readonly string[], ready = true, scope = ''): ReadonlySet<string> {
  const seen = useRef<Set<string> | null>(null)
  const seenScope = useRef(scope)
  const [fresh, setFresh] = useState<ReadonlySet<string>>(() => new Set())
  const key = ids.join(',')
  useEffect(() => {
    if (!ready) return
    if (seen.current === null || seenScope.current !== scope) {
      seenScope.current = scope
      // The first list is what was already there: nothing arrives.
      seen.current = new Set(ids)
      return
    }
    const added = ids.filter((id) => !seen.current!.has(id))
    for (const id of ids) seen.current.add(id)
    if (added.length === 0) return
    setFresh((prev) => new Set([...prev, ...added]))
    const timer = setTimeout(
      () =>
        setFresh((prev) => {
          const next = new Set(prev)
          for (const id of added) next.delete(id)
          return next
        }),
      ARRIVAL_MS,
    )
    return () => clearTimeout(timer)
    // `key` stands for `ids`: a new array with the same ids is no change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready, scope])
  return fresh
}

/** The class a row gets while it is in `useArrivals`. */
export const arrivalClass = 'animate-arrive'
